/**
 * «Пересоздать карточки» — мастер по выбранным товарам или макетам.
 *
 * Те же три вопроса, что и в карточке макета: что перегенерировать, в какие
 * кабинеты и по каким контент-воронкам. Отличие одно и оно в начале: там выбор
 * идёт от матрицы одного макета, здесь — от товаров, а значит от нескольких
 * макетов сразу.
 *
 * Что происходит после запуска: конвейер рендерит визуалы выбранных воронок,
 * генерирует тексты SEO для кабинетов прогона и пересобирает черновики карточек
 * маркетплейсов на свежую ревизию. Файлы печати переиспользуются.
 *
 * Прогоны уходят по одному. 17.08.2026 одиннадцать одновременных запросов к
 * media-generation-service совпали с его перезапуском и отвалились по таймауту —
 * с тех пор очередь, а не веер.
 *
 * Очередь к тому же идёт с паузой и повтором (rateLimitedCall.ts): nginx на
 * проде (`limit_req zone=api rate=10r/s burst=20`, по IP) отбивает частые
 * запросы кодом 503, и 01.10.2026 на этом падала выборка уже из 28 макетов.
 *
 * Планы товаров и воронок с 02.10.2026 идут пачками по PLAN_BATCH_SIZE макетов:
 * 74 макета — два запроса на шаг вместо 74. Поштучным остался только запуск —
 * он создаёт задачи, и склеивать его в один запрос рискованнее, чем ждать.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Check, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { Stepper, type Step } from '../ui/Stepper'
import ContentFunnelSelectionStep from '../ContentFunnelSelectionStep'
import {
  initialFunnelChoices,
  serializeFunnelSelections,
} from '../contentFunnelSelection.logic'
import type { FunnelPlanRow } from '../contentFunnelSelection.types'
import { pipelineAPI } from '../../features/pipeline/api'
import {
  activeDesigns,
  cabinetKey,
  chunked,
  initialCategories,
  mergeCabinets,
  mergeFunnelRows,
  PLAN_BATCH_SIZE,
  selectedTargets,
  selectionSummary,
  toggleCategory,
  unpackBatch,
  type DesignPlanEntry,
} from './rebuildCardsWizard.logic'
import { createPacedCaller } from './rateLimitedCall'

type WizardStep = 1 | 2 | 3 | 4
type LoadState = 'idle' | 'loading' | 'ready' | 'error'

const STEPS: Step[] = [
  { id: 'products', label: 'Товары', description: 'По матрице категорий' },
  { id: 'cabinets', label: 'Кабинеты', description: 'Куда пойдут визуалы' },
  { id: 'funnels', label: 'Контент-воронки', description: 'Чем рендерить' },
  { id: 'run', label: 'Выполнение', description: 'Запуск прогонов' },
]

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error?.message || error?.message || fallback

export interface RebuildCardsWizardProps {
  isOpen: boolean
  /** Коды макетов выбранных товаров. */
  designCodes: string[]
  /** Артикулы выборки: по ним предвыбираются категории. */
  articles: string[]
  /** Товары без макета — в прогон не идут, но сказать о них надо. */
  withoutDesignCount?: number
  onClose: () => void
  /** Прогоны запущены — каталог стоит перечитать. */
  onLaunched: () => void
}

export function RebuildCardsWizard({
  isOpen,
  designCodes,
  articles,
  withoutDesignCount = 0,
  onClose,
  onLaunched,
}: RebuildCardsWizardProps) {
  const [step, setStep] = useState<WizardStep>(1)
  const [entries, setEntries] = useState<DesignPlanEntry[]>([])
  const [planState, setPlanState] = useState<LoadState>('idle')
  const [planError, setPlanError] = useState('')
  const [planLoaded, setPlanLoaded] = useState(0)
  const [categories, setCategories] = useState<Record<string, number[]>>({})
  const [cabinets, setCabinets] = useState<string[]>([])
  const [funnelRowsByDesign, setFunnelRowsByDesign] = useState<Record<string, FunnelPlanRow[]>>({})
  const [funnelPlanMeta, setFunnelPlanMeta] = useState<{ canonical_targets: any[]; funnels: any[] }>(
    { canonical_targets: [], funnels: [] },
  )
  const [funnelState, setFunnelState] = useState<LoadState>('idle')
  const [funnelError, setFunnelError] = useState('')
  const [funnelLoaded, setFunnelLoaded] = useState({ done: 0, total: 0 })
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState('')
  const [progress, setProgress] = useState<{ done: number; failed: number; total: number } | null>(
    null,
  )

  // Из каталога макетов артикулов не приходит: там выделяют сами макеты, и
  // предвыбор берёт все категории матрицы — прежнее поведение кнопки.
  const byProducts = articles.length > 0

  const codesKey = designCodes.join(',')

  const loadPlans = useCallback(async () => {
    setPlanState('loading')
    setPlanError('')
    setPlanLoaded(0)
    try {
      const call = createPacedCaller()
      const loaded: DesignPlanEntry[] = []
      for (const chunk of chunked(designCodes, PLAN_BATCH_SIZE)) {
        const items = await call(() => pipelineAPI.getRegenerationPlansBatch(chunk))
        for (const { code, result } of unpackBatch(items)) loaded.push({ code, plan: result })
        setPlanLoaded(loaded.length)
      }
      setEntries(loaded)
      setCategories(initialCategories(loaded, articles))
      const merged = mergeCabinets(loaded)
      setCabinets(merged.filter((cabinet) => cabinet.used_before).map(cabinetKey))
      setPlanState('ready')
    } catch (error: any) {
      setEntries([])
      setPlanState('error')
      setPlanError(errorMessage(error, 'Не удалось загрузить товары и кабинеты макетов'))
    }
  }, [articles, designCodes])

  useEffect(() => {
    if (!isOpen || !codesKey) return
    setStep(1)
    setFunnelRowsByDesign({})
    setFunnelState('idle')
    setFunnelError('')
    setChoices({})
    setLaunchError('')
    setProgress(null)
    void loadPlans()
    // Перечитываем планы при новой выборке, а не на каждый рендер.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, codesKey])

  const mergedCabinets = useMemo(() => mergeCabinets(entries), [entries])
  const targets = useMemo(
    () => selectedTargets(mergedCabinets, cabinets),
    [mergedCabinets, cabinets],
  )
  const summary = useMemo(() => selectionSummary(entries, categories), [entries, categories])
  const funnelRows = useMemo(() => mergeFunnelRows(funnelRowsByDesign), [funnelRowsByDesign])
  const selections = useMemo(
    () => serializeFunnelSelections(funnelRows, choices),
    [funnelRows, choices],
  )

  const loadFunnels = useCallback(async () => {
    setFunnelState('loading')
    setFunnelError('')
    try {
      const rows: Record<string, FunnelPlanRow[]> = {}
      let meta = { canonical_targets: [] as any[], funnels: [] as any[] }
      const designs = activeDesigns(entries, categories)
      const call = createPacedCaller()
      const funnelTargets = targets.map(({ marketplace, seller_id }) => ({ marketplace, seller_id }))
      setFunnelLoaded({ done: 0, total: designs.length })
      for (const chunk of chunked(designs, PLAN_BATCH_SIZE)) {
        const items = await call(() => pipelineAPI.planRegenerationFunnelsBatch(
          chunk.map((entry) => ({
            design_code: entry.code,
            category_ids: categories[entry.code] || [],
          })),
          funnelTargets,
        ))
        for (const { code, result: plan } of unpackBatch(items)) {
          rows[code] = plan.rows as FunnelPlanRow[]
          meta = { canonical_targets: plan.canonical_targets, funnels: plan.funnels }
        }
        setFunnelLoaded({ done: Object.keys(rows).length, total: designs.length })
      }
      setFunnelRowsByDesign(rows)
      setFunnelPlanMeta(meta)
      setChoices(initialFunnelChoices(mergeFunnelRows(rows)))
      setFunnelState('ready')
      setStep(3)
    } catch (error: any) {
      setFunnelState('error')
      setFunnelError(errorMessage(error, 'Не удалось подобрать контент-воронки'))
    }
  }, [categories, entries, targets])

  const launch = useCallback(async () => {
    const designs = activeDesigns(entries, categories)
    if (!designs.length) return
    setLaunching(true)
    setLaunchError('')
    setStep(4)
    setProgress({ done: 0, failed: 0, total: designs.length })
    let ok = 0
    let failed = 0
    const call = createPacedCaller()
    for (const entry of designs) {
      try {
        await call(() => pipelineAPI.regenerateSelectedVisuals(entry.code, {
          categoryIds: categories[entry.code] || [],
          targets,
          funnelSelections: serializeFunnelSelections(
            funnelRowsByDesign[entry.code] || [],
            choices,
          ) as any,
        }))
        ok++
      } catch (error: any) {
        failed++
        if (!failed || failed === 1) {
          setLaunchError(errorMessage(error, `Макет #${entry.code}: прогон не запустился`))
        }
      }
      setProgress({ done: ok, failed, total: designs.length })
    }
    setLaunching(false)
    onLaunched()
  }, [categories, choices, entries, funnelRowsByDesign, onLaunched, targets])

  const footer = (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-xs text-gray-500 dark:text-zinc-400">
        {step === 4
          ? 'Прогоны идут в фоне — окно можно закрыть'
          : `Макетов: ${summary.designs} · товаров: ${summary.products}`
            + (summary.created ? ` · будет создано: ${summary.created}` : '')}
      </span>
      {step === 4 ? (
        <Button variant="primary" onClick={onClose} disabled={launching}>
          {launching ? 'Запускаем…' : 'Закрыть'}
        </Button>
      ) : step === 3 ? (
        <div className="flex gap-2 sm:ml-auto">
          <Button variant="secondary" onClick={() => setStep(2)}>
            Назад
          </Button>
          <Button
            variant="primary"
            disabled={selections.length === 0}
            onClick={() => void launch()}
          >
            Пересобрать
          </Button>
        </div>
      ) : (
        <div className="flex gap-2 sm:ml-auto">
          <Button variant="secondary" onClick={step === 1 ? onClose : () => setStep(1)}>
            {step === 1 ? 'Отмена' : 'Назад'}
          </Button>
          <Button
            variant="primary"
            loading={funnelState === 'loading'}
            disabled={
              planState !== 'ready'
              || summary.designs === 0
              || (step === 2 && targets.length === 0)
            }
            onClick={() => {
              if (step === 1) setStep(2)
              else void loadFunnels()
            }}
          >
            Далее
          </Button>
        </div>
      )}
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={launching ? () => undefined : onClose}
      title="Пересоздать карточки"
      subtitle={byProducts
        ? `Выбрано товаров: ${articles.length} · макетов: ${designCodes.length}`
        : `Выбрано макетов: ${designCodes.length}`}
      size="xl"
      footer={footer}
      closeOnOverlayClick={false}
      closeOnEsc={!launching}
      showCloseButton={!launching}
    >
      <div className="flex gap-6">
        <div className="hidden w-44 shrink-0 pt-1 md:block">
          <Stepper
            steps={STEPS}
            currentIndex={step - 1}
            onStepClick={(index) => {
              if (step === 4) return
              const target = (index + 1) as WizardStep
              if (target < step) setStep(target)
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          {planState === 'loading' && (
            <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Читаем матрицы выбранных макетов… {planLoaded} из {designCodes.length}
            </p>
          )}
          {planState === 'error' && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              {planError}
            </div>
          )}

          {step === 1 && planState === 'ready' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                  Что перегенерировать
                </h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  {byProducts
                    ? 'Отмечены категории, в которых лежат выбранные товары. '
                    : 'Отмечены все категории матрицы выбранных макетов. '}
                  Единица выбора — категория: визуалы многотоварной категории движок рендерит
                  одной задачей на все её артикулы.
                </p>
              </div>
              {withoutDesignCount > 0 && (
                <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                  Товаров без макета в выборке: {withoutDesignCount}. Визуалы рендерятся из
                  макета, поэтому в прогон они не идут.
                </p>
              )}
              <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
                {entries.map((entry) => (
                  <div
                    key={entry.code}
                    className="rounded-xl border border-gray-200 p-3 dark:border-zinc-700"
                  >
                    <p className="mb-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
                      Макет #{entry.code}
                      {entry.plan.design_type ? ` · ${entry.plan.design_type}` : ''}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(entry.plan.categories || []).map((category) => {
                        const checked = (categories[entry.code] || []).includes(
                          category.category_id,
                        )
                        return (
                          <label
                            key={category.category_id}
                            className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 text-xs hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              aria-label={`${entry.code}: ${category.category_description}`}
                              onChange={() =>
                                setCategories((current) =>
                                  toggleCategory(current, entry.code, category.category_id),
                                )
                              }
                              className="h-4 w-4 accent-indigo-600"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-gray-900 dark:text-zinc-100">
                                {category.category_description}
                              </span>
                              <span className="block text-gray-500 dark:text-zinc-400">
                                товаров {Math.max(1, category.quantity)}
                                {category.missing > 0 ? ` · создать ${category.missing}` : ''}
                              </span>
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && planState === 'ready' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Кабинеты</h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  Отмечены кабинеты, которые уже участвовали в прогонах выбранных макетов. Новый
                  кабинет получит визуалы и черновик карточки впервые.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {mergedCabinets.map((cabinet) => {
                  const key = cabinetKey(cabinet)
                  const checked = cabinets.includes(key)
                  return (
                    <label
                      key={key}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition-colors ${
                        checked
                          ? 'border-indigo-400 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/30'
                          : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        aria-label={`${cabinet.marketplace.toUpperCase()}, ${cabinet.name}, кабинет ${cabinet.cabinet}`}
                        onChange={() =>
                          setCabinets((current) =>
                            current.includes(key)
                              ? current.filter((value) => value !== key)
                              : [...current, key],
                          )
                        }
                        className="h-4 w-4 accent-indigo-600"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-gray-900 dark:text-zinc-100">
                          {cabinet.name}
                        </span>
                        <span className="block truncate text-xs text-gray-500 dark:text-zinc-400">
                          {cabinet.marketplace.toUpperCase()} · кабинет {cabinet.cabinet}
                        </span>
                      </span>
                      {cabinet.used_before && (
                        <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-2xs text-gray-600 dark:bg-zinc-800 dark:text-zinc-300">
                          был в прогоне
                        </span>
                      )}
                    </label>
                  )
                })}
              </div>
              {mergedCabinets.length === 0 && (
                <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                  Активных кабинетов нет — пересобирать карточки некуда.
                </p>
              )}
              {funnelState === 'loading' && (
                <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Подбираем контент-воронки… {funnelLoaded.done} из {funnelLoaded.total}
                </p>
              )}
              {targets.length === 0 && mergedCabinets.length > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  Отметьте хотя бы один кабинет.
                </p>
              )}
              {funnelState === 'error' && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  {funnelError}
                </div>
              )}
            </div>
          )}

          {step === 3 && funnelState === 'ready' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                Вопрос задаётся один раз на пару «категория × кабинет»: у макетов одного типа
                строки плана совпадают, и выбор применяется ко всем.
              </p>
              <ContentFunnelSelectionStep
                plan={{
                  canonical_targets: funnelPlanMeta.canonical_targets as any,
                  rows: funnelRows,
                  funnels: funnelPlanMeta.funnels,
                }}
                choices={choices}
                onChoiceChange={(rowKey, value) =>
                  setChoices((current) => ({ ...current, [rowKey]: value }))
                }
              />
              {selections.length === 0 && (
                <p className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-4 w-4" />
                  Ни одна воронка не выбрана — генерировать нечего.
                </p>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3 text-sm text-gray-700 dark:text-zinc-200">
              <p className="flex items-center gap-2">
                {launching ? (
                  <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-indigo-500" />
                ) : (
                  <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                )}
                <span>
                  {launching ? 'Запускаем прогоны: ' : 'Запущено прогонов: '}
                  <b className="tabular-nums">
                    {progress?.done ?? 0} из {progress?.total ?? 0}
                  </b>
                </span>
              </p>
              {(progress?.failed || 0) > 0 && (
                <p className="text-red-600 dark:text-red-400">
                  Не удалось запустить: {progress?.failed}
                  {launchError ? ` — ${launchError}` : ''}
                </p>
              )}
              <p className="text-gray-500 dark:text-zinc-400">
                Рендер идёт в фоне и занимает минуты на макет. Дальше конвейер сам сгенерирует
                тексты SEO и пересоберёт черновики карточек. За ходом работ — индикатор фоновых
                задач, состояние макета — в его карточке.
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
